import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, Copy, Power } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Loader, Chip } from "@/components/common";
import { PageTitle, Table } from "@/pages/admin/kit";
import { RichTextEditor } from "@/components/RichText";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CMS_FIELDS = [
  ["land.badge", "Hero-Badge (oben)"],
  ["hero.title", "Hero-Überschrift"],
  ["hero.sub", "Hero-Untertext"],
  ["land.marquee", "Laufband-Text"],
  ["land.benefits_title", "Vorteile – Überschrift"],
  ["how.title", "So funktioniert's – Überschrift"],
  ["how.1", "Schritt 1"], ["how.2", "Schritt 2"], ["how.3", "Schritt 3"], ["how.4", "Schritt 4"],
  ["land.provider_title", "Waschheld-Block – Überschrift"],
  ["land.provider_sub", "Waschheld-Block – Text"],
  ["land.cta_title", "Abschluss-CTA – Überschrift"],
  ["land.cta_sub", "Abschluss-CTA – Text"],
];

export function Content() {
  const [ov, setOv] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/content").then((r) => setOv(r.data.overrides || {})).catch((e) => toast.error(errMsg(e))); }, []);
  if (!ov) return <Loader />;
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/content", { overrides: ov }); toast.success("Inhalte gespeichert"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageTitle sub="Texte der Startseite anpassen · leer lassen = Standardtext verwenden">Inhalte</PageTitle>
      <div className="space-y-4 max-w-2xl">
        {CMS_FIELDS.map(([k, label]) => (
          <label key={k} className="block"><span className="w-label">{label}</span>
            <textarea className="w-input !h-auto py-2.5" rows={k.includes("sub") || k.startsWith("how.") ? 2 : 1} value={ov[k] || ""} onChange={(e) => setOv({ ...ov, [k]: e.target.value })} data-testid={`cms-${k}`} /></label>
        ))}
        <button className="w-btn w-btn-primary" onClick={save} disabled={busy} data-testid="cms-save">{busy ? "Speichert…" : "Inhalte speichern"}</button>
      </div>
    </div>
  );
}

const EMPTY = { title: "", excerpt: "", image_url: "", body: "", status: "draft" };

export function Blog() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/blog").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!items) return <Loader />;
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit.id) await api.patch(`/admin/blog/${edit.id}`, edit);
      else await api.post("/admin/blog", edit);
      toast.success("Gespeichert"); setEdit(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/blog/${id}`); toast.success("Gelöscht"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageTitle sub="Beiträge schreiben & veröffentlichen · öffentlich unter /blog">Blog</PageTitle>
      <button className="w-btn w-btn-primary mb-4" onClick={() => setEdit({ ...EMPTY })} data-testid="blog-new"><Plus size={18} aria-hidden="true" /> Neuer Beitrag</button>
      <Table testId="admin-blog-table" rows={items} cols={[
        ["Titel", (p) => <div><p className="font-semibold">{p.title}</p><p className="w-muted text-sm">/{p.slug}</p></div>],
        ["Status", (p) => <Chip tone={p.status === "published" ? "mint" : "neutral"}>{p.status === "published" ? "veröffentlicht" : "Entwurf"}</Chip>],
        ["Aktualisiert", (p) => fmtDateTime(p.updated_at)],
        ["Aktionen", (p) => (
          <div className="flex gap-2">
            <button className="w-btn w-btn-secondary !min-h-[38px] !px-4" onClick={() => setEdit({ ...p })} data-testid={`blog-edit-${p.id}`}>Bearbeiten</button>
            {p.status === "published" && <a className="w-btn w-btn-secondary !min-h-[38px] !px-3" href={`/blog/${p.slug}`} target="_blank" rel="noreferrer"><ExternalLink size={15} aria-hidden="true" /></a>}
            <button className="w-btn w-btn-danger !min-h-[38px] !px-3" onClick={() => del(p.id)} data-testid={`blog-del-${p.id}`}><Trash2 size={15} aria-hidden="true" /></button>
          </div>
        )],
      ]} />
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>{edit?.id ? "Beitrag bearbeiten" : "Neuer Beitrag"}</DialogTitle></DialogHeader>
          {edit && (
            <form className="space-y-3" onSubmit={submit}>
              <label className="block"><span className="w-label">Titel</span><input className="w-input" required value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="blog-title" /></label>
              <label className="block"><span className="w-label">Kurzbeschreibung</span><input className="w-input" value={edit.excerpt} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} data-testid="blog-excerpt" /></label>
              <label className="block"><span className="w-label">Bild-URL (optional)</span><input className="w-input" value={edit.image_url} onChange={(e) => setEdit({ ...edit, image_url: e.target.value })} data-testid="blog-image" /></label>
              <div className="block"><span className="w-label">Text</span>
                <RichTextEditor key={edit.id || "new"} value={edit.body} onChange={(html) => setEdit({ ...edit, body: html })} testId="blog-body" minHeight="14rem" /></div>
              <label className="block"><span className="w-label">Status</span>
                <select className="w-input" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="blog-status"><option value="draft">Entwurf</option><option value="published">Veröffentlicht</option></select></label>
              <button className="w-btn w-btn-primary w-full" data-testid="blog-save">Speichern</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const LEGAL_DOCS = [["agb", "AGB"], ["datenschutz", "Datenschutzerklärung"], ["impressum", "Impressum"], ["widerruf", "Widerrufsbelehrung"]];

export function Legal() {
  const [slug, setSlug] = useState("agb");
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDoc(null); api.get(`/admin/legal/${slug}`).then((r) => setDoc(r.data)).catch((e) => toast.error(errMsg(e))); }, [slug]);
  const save = async (publish) => {
    setBusy(true);
    try {
      const payload = { title: doc.title, body: doc.body, published: publish ?? doc.published };
      const { data } = await api.put(`/admin/legal/${slug}`, payload);
      setDoc({ ...doc, published: data.published, version: data.version });
      toast.success(data.published ? "Gespeichert & veröffentlicht" : "Als Entwurf gespeichert");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageTitle sub="Datenschutz, AGB, Impressum & Widerruf verfassen · erst nach Veröffentlichung öffentlich sichtbar">Rechtstexte</PageTitle>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="legal-tabs">
        {LEGAL_DOCS.map(([s, l]) => (
          <button key={s} className={`w-chip ${slug === s ? "bg-mint-ink text-white" : "bg-gray-100"}`} onClick={() => setSlug(s)} data-testid={`legal-tab-${s}`}>{l}</button>
        ))}
      </div>
      {!doc ? <Loader /> : (
        <div className="space-y-4 max-w-3xl">
          <div className="flex items-center gap-3 flex-wrap">
            <Chip tone={doc.published ? "mint" : "neutral"}>{doc.published ? "Veröffentlicht" : "Entwurf / nicht öffentlich"}</Chip>
            <span className="w-muted text-sm">Version {doc.version || 0}{doc.updated_at ? ` · ${fmtDateTime(doc.updated_at)}` : ""}</span>
            {doc.published && <a className="w-btn w-btn-secondary !min-h-[34px] !px-3" href={`/${slug}`} target="_blank" rel="noreferrer"><ExternalLink size={14} aria-hidden="true" /> Ansehen</a>}
          </div>
          <label className="block"><span className="w-label">Titel</span><input className="w-input" value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} data-testid="legal-title" /></label>
          <div className="block"><span className="w-label">Inhalt</span>
            <RichTextEditor key={slug} value={doc.body} onChange={(html) => setDoc({ ...doc, body: html })} testId="legal-body" minHeight="24rem" /></div>
          <div className="flex flex-wrap gap-2">
            <button className="w-btn w-btn-secondary" onClick={() => save(false)} disabled={busy} data-testid="legal-save-draft">Als Entwurf speichern</button>
            <button className="w-btn w-btn-primary" onClick={() => save(true)} disabled={busy} data-testid="legal-publish">Speichern & veröffentlichen</button>
            {doc.published && <button className="w-btn w-btn-secondary" onClick={() => save(false)} disabled={busy} data-testid="legal-unpublish">Veröffentlichung zurückziehen</button>}
          </div>
          <p className="w-muted text-sm">Hinweis: Rechtstexte vor Veröffentlichung anwaltlich prüfen lassen. Änderungen werden versioniert.</p>
        </div>
      )}
    </div>
  );
}

export function WebsiteStatus() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmTxt, setConfirmTxt] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const doReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/admin/reset-demo-data", { confirm: confirmTxt });
      const d = data.deleted || {};
      toast.success(`Zurückgesetzt: ${d.users || 0} Konten, ${d.bookings || 0} Buchungen, ${d.payments || 0} Zahlungen gelöscht`);
      setResetOpen(false); setConfirmTxt("");
    } catch (e) { toast.error(errMsg(e)); } finally { setResetBusy(false); }
  };
  const load = () => api.get("/admin/coming-soon").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const save = async (enabled) => {
    setBusy(true);
    try {
      const payload = { enabled, title: cfg.title, message: cfg.message };
      const { data } = await api.put("/admin/coming-soon", payload);
      setCfg({ ...cfg, enabled, preview_token: data.preview_token });
      toast.success(enabled ? "Coming-Soon-Modus aktiviert" : "Website ist jetzt live");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const previewUrl = cfg?.preview_token ? `${window.location.origin}/?preview=${cfg.preview_token}` : "";
  const copyPreview = () => { navigator.clipboard.writeText(previewUrl); toast.success("Vorschau-Link kopiert"); };
  if (!cfg) return <Loader />;
  return (
    <div>
      <PageTitle sub="Zeige Besuchern vorübergehend eine „Demnächst verfügbar“-Seite · du selbst siehst die echte Website weiterhin">Website-Status</PageTitle>
      <div className="space-y-5 max-w-3xl">
        <div className="w-card p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="coming-soon-status-card">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full grid place-items-center ${cfg.enabled ? "bg-amber-100 text-amber-700" : "bg-mint-soft text-mint-ink"}`}><Power size={20} aria-hidden="true" /></div>
            <div>
              <div className="font-bold">{cfg.enabled ? "Coming-Soon-Modus ist AKTIV" : "Website ist LIVE"}</div>
              <div className="w-muted text-sm">{cfg.enabled ? "Besucher sehen die „Demnächst verfügbar“-Seite." : "Alle Besucher sehen die normale Website."}</div>
            </div>
          </div>
          {cfg.enabled ? (
            <button className="w-btn w-btn-primary" onClick={() => save(false)} disabled={busy} data-testid="coming-soon-golive">Website scharfschalten</button>
          ) : (
            <button className="w-btn w-btn-secondary" onClick={() => save(true)} disabled={busy} data-testid="coming-soon-activate">Coming-Soon aktivieren</button>
          )}
        </div>

        <div className="w-card p-5 space-y-4">
          <label className="block"><span className="w-label">Überschrift</span>
            <input className="w-input" value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} data-testid="coming-soon-title-input" /></label>
          <label className="block"><span className="w-label">Text</span>
            <textarea className="w-input !h-24 py-2.5" value={cfg.message} onChange={(e) => setCfg({ ...cfg, message: e.target.value })} data-testid="coming-soon-message-input" /></label>
          <button className="w-btn w-btn-secondary" onClick={() => save(cfg.enabled)} disabled={busy} data-testid="coming-soon-save">Text speichern</button>
        </div>

        <div className="w-card p-5 space-y-3">
          <div className="font-bold">Geheimer Vorschau-Link</div>
          <p className="w-muted text-sm">Öffne diesen Link (auch ohne Login), um die echte Website zu sehen, während der Coming-Soon-Modus aktiv ist. Zum Teilen mit Partnern geeignet.</p>
          <div className="flex gap-2">
            <input className="w-input font-mono text-sm" readOnly value={previewUrl} data-testid="coming-soon-preview-url" />
            <button className="w-btn w-btn-secondary !px-3" onClick={copyPreview} data-testid="coming-soon-copy-preview"><Copy size={16} aria-hidden="true" /></button>
          </div>
        </div>

        <div className="w-card p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-bold">Interessenten (Warteliste)</div>
            <div className="w-muted text-sm">E-Mail-Adressen, die sich für den Start vormerken.</div>
          </div>
          <Chip tone="mint" data-testid="coming-soon-waitlist-count">{cfg.waitlist_count} Anmeldungen</Chip>
        </div>

        <div className="w-card p-5 space-y-3 border border-red-200" data-testid="reset-demo-card">
          <div className="font-bold text-red-700">Launch vorbereiten · Demo-Daten zurücksetzen</div>
          <p className="w-muted text-sm">Löscht alle Demo-Waschhelden sowie automatische Test-Konten und entfernt <b>alle</b> Buchungen, Zahlungen, Nachrichten, Reklamationen und Bewertungen. Staff- und echte Konten bleiben erhalten. Diese Aktion kann nicht rückgängig gemacht werden.</p>
          <button className="w-btn w-btn-secondary !text-red-700 !border-red-300" onClick={() => setResetOpen(true)} data-testid="reset-demo-open"><Trash2 size={16} aria-hidden="true" /> Demo-Daten zurücksetzen</button>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-demo-dialog">
          <DialogHeader><DialogTitle>Demo-Daten endgültig löschen?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm w-muted">Es werden alle Demo-/Test-Konten und sämtliche Buchungen, Zahlungen, Nachrichten, Reklamationen und Bewertungen unwiderruflich gelöscht. Gib zur Bestätigung <b>LÖSCHEN</b> ein.</p>
            <input className="w-input" value={confirmTxt} onChange={(e) => setConfirmTxt(e.target.value)} placeholder="LÖSCHEN" data-testid="reset-demo-confirm-input" />
            <div className="flex justify-end gap-2">
              <button className="w-btn w-btn-ghost" onClick={() => setResetOpen(false)} data-testid="reset-demo-cancel">Abbrechen</button>
              <button className="w-btn w-btn-primary" onClick={doReset} disabled={resetBusy || confirmTxt.trim().toUpperCase() !== "LÖSCHEN"} data-testid="reset-demo-confirm">Endgültig löschen</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
